// src/components/sourcex/ConditionBuilder.jsx
//
// SourceX template editor — the "show this only when …" rule builder.
//
// Extracted out of SectionEditor.js so both the document's indent-arrow /
// branch-icon popover AND QuestionBuilder's embedded Conditional section
// (add/edit a question's visibility rule without leaving the builder) can
// use the exact same component. Framework-light: it's handed the candidate
// question lists and a couple of small lookup helpers by whoever renders it,
// rather than reaching into SectionEditor's state itself.

import { useState } from "react";
import { GitBranch, Trash2, Plus, X } from "lucide-react";
import { inputCls, selectCls } from "./QuestionField";
import {
  CONDITION_OPS,
  OP_LABELS,
  VALUELESS_OPS,
  MULTI_VALUE_OPS,
  PLURAL_OP,
  SINGULAR_OP,
  isChoiceOp,
} from "../../services/templateSchema";

// The answer choices a controlling question can be compared against, so the
// rule builder offers real options to click instead of a free-text box the
// user has to spell exactly right (a typo there silently hides the field
// forever). An empty list means "no fixed choices" — the rule takes a typed
// value. Exported: SectionEditor.js also uses this to pick a sensible
// default controlling question (one that actually has choices).
export const choicesForQuestion = (q) => {
  if (!q) return [];
  if (q.type === "Select" || q.type === "MultiSelect") {
    return (Array.isArray(q.options) ? q.options : []).map((o) => ({
      value: String(o),
      label: String(o),
    }));
  }
  if (q.type === "YesNo/Maybe") {
    return ["Yes", "No", "N/A"].map((v) => ({ value: v, label: v }));
  }
  if (q.type === "Checkbox") {
    return [
      { value: "true", label: "Checked" },
      { value: "false", label: "Unchecked" },
    ];
  }
  return [];
};

const isMultiAnswer = (q) => q?.type === "MultiSelect";

// Which operators make sense for a given controlling field. Offering "greater
// than" on a dropdown is how a coordinator ends up with a rule that never
// fires, so the list is narrowed to what can actually match.
const opsForQuestion = (q) => {
  const base = ["eq", "neq", "in", "notIn"];
  if (!q) return CONDITION_OPS;
  if (q.type === "MultiSelect") return [...base, "contains", "isBlank", "notBlank"];
  if (q.type === "Number") {
    return [...base, "gt", "gte", "lt", "lte", "isBlank", "notBlank"];
  }
  if (q.type === "Text" || q.type === "TextArea") {
    return [...base, "contains", "isBlank", "notBlank"];
  }
  return [...base, "isBlank", "notBlank"];
};

/**
 * Visibility-rule builder for one field: "show this only when …".
 *
 * Deliberately a FLAT list with one ANY/ALL switch rather than a nested tree —
 * that covers every branch a source document actually needs, and it makes the
 * one thing coordinators kept getting wrong ("either Yes should reveal it")
 * a single visible toggle instead of an invisible structural detail.
 */
export default function ConditionBuilder({
  mode,
  rules,
  complex,
  candidates,
  allCandidates = [],
  sectionLabelOf,
  currentSectionLabel,
  defaultQuestionId,
  onChange,
  onClear,
}) {
  // Reaching into another section is possible but deliberate. Almost every
  // rule is about a question in the same block, so that is what the picker
  // offers until the user asks for more.
  const [lookOutside, setLookOutside] = useState(false);

  // A rule that ALREADY points outside this section (an AI-generated
  // sex → pregnancy-test branch, say) forces the wider list on, so its own
  // value is always present in the dropdown and can't be silently lost.
  const scopedIds = new Set(candidates.map((c) => c.id));
  const hasOutOfSectionRule = rules.some(
    (r) => r.questionId && !scopedIds.has(r.questionId)
  );
  const listAll = lookOutside || hasOutOfSectionRule;
  const pickList = listAll && allCandidates.length ? allCandidates : candidates;

  // Keyed on the WIDEST list, so a cross-section rule still resolves its type,
  // options and operators even while the picker is scoped.
  const byId = new Map(
    (allCandidates.length ? allCandidates : candidates).map((q) => [q.id, q])
  );

  // Options grouped by heading when the wider list is showing — a flat list of
  // every field in a visit is the wall this scoping exists to avoid. The
  // current section is listed first, since that's still the likely answer.
  const optionGroups = (() => {
    if (!listAll || !sectionLabelOf) return null;
    const groups = new Map();
    pickList.forEach((c) => {
      const label = sectionLabelOf(c.id);
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label).push(c);
    });
    return [...groups.entries()].sort(
      (a, b) =>
        (b[0] === currentSectionLabel ? 1 : 0) -
        (a[0] === currentSectionLabel ? 1 : 0)
    );
  })();

  const setRule = (i, patch) =>
    onChange(
      mode,
      rules.map((r, ri) => (ri === i ? { ...r, ...patch } : r))
    );
  const removeRule = (i) =>
    onChange(
      mode,
      rules.filter((_, ri) => ri !== i)
    );

  // Which question a NEW rule starts on. Defaulting to the first field in the
  // whole visit was almost never right — skip logic reads "if the question
  // just above this one was answered X". So the default is the nearest
  // preceding field with answers to choose from (computed by the parent),
  // falling back to the first available only when nothing precedes it.
  const seedPool = candidates.length ? candidates : allCandidates;
  const seedQuestionId =
    defaultQuestionId && seedPool.some((c) => c.id === defaultQuestionId)
      ? defaultQuestionId
      : seedPool[0]?.id || "";

  const addRule = () => {
    // A second condition is nearly always another acceptable answer to the
    // SAME question ("or Yes, resolved"), so it inherits the last rule's
    // question rather than jumping elsewhere.
    const lastRule = rules[rules.length - 1];
    const startOn =
      lastRule?.questionId && byId.has(lastRule.questionId)
        ? lastRule.questionId
        : seedQuestionId;
    onChange(mode, [...rules, { questionId: startOn, op: "eq", value: "" }]);
  };

  if (complex) {
    return (
      <div className="rounded-xl border border-fuchsia-400/30 bg-fuchsia-400/[0.07] p-3 space-y-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-fuchsia-200">
          <GitBranch className="w-3.5 h-3.5" /> Advanced rule
        </div>
        <p className="text-[11px] text-fuchsia-100/80">
          Too complex to show here, so it's been left untouched. Start over to
          rebuild it in plain language.
        </p>
        <button
          type="button"
          onClick={onClear}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-rose-200 bg-rose-500/15 border border-rose-400/30 hover:bg-rose-500/25 transition"
        >
          <Trash2 className="w-3 h-3" /> Delete this rule and start over
        </button>
      </div>
    );
  }

  const on = rules.length > 0;

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 space-y-2">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-purple-200">
        <GitBranch className="w-3.5 h-3.5" /> When should this field appear?
      </div>

      {/* The whole feature in one choice, in words. Everything below only
          appears once the user has said they want a condition at all. */}
      <div className="grid sm:grid-cols-2 gap-1.5">
        <button
          type="button"
          onClick={onClear}
          className={`text-left rounded-lg border px-2.5 py-2 transition ${
            !on
              ? "border-purple-300/50 bg-purple-500/20"
              : "border-white/10 bg-white/[0.03] hover:bg-white/[0.07]"
          }`}
        >
          <span className="block text-xs font-medium text-slate-100">
            Always shown
          </span>
        </button>
        <button
          type="button"
          onClick={() => {
            if (on) return;
            // Nothing in this section to branch on, but the visit has fields —
            // open the wider list rather than handing back an empty picker.
            if (!candidates.length && allCandidates.length) setLookOutside(true);
            onChange(mode, [{ questionId: seedQuestionId, op: "eq", value: "" }]);
          }}
          disabled={seedPool.length === 0}
          className={`text-left rounded-lg border px-2.5 py-2 transition disabled:opacity-40 disabled:cursor-not-allowed ${
            on
              ? "border-purple-300/50 bg-purple-500/20"
              : "border-white/10 bg-white/[0.03] hover:bg-white/[0.07]"
          }`}
        >
          <span className="block text-xs font-medium text-slate-100">
            Only after a certain answer
          </span>
        </button>
      </div>

      {!on ? (
        seedPool.length === 0 && (
          <p className="text-[10px] text-slate-500">
            There are no other fields in this visit to base a condition on yet.
          </p>
        )
      ) : (
        <>
          {rules.length > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-slate-400">How many must be true?</span>
              <div className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.04] p-0.5">
                {[
                  ["any", "Just one"],
                  ["all", "All of them"],
                ].map(([m, label]) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => onChange(m, rules)}
                    className={`px-2 py-1 rounded-md text-[11px] transition ${
                      mode === m
                        ? "bg-purple-500/30 text-white"
                        : "text-slate-400 hover:text-white hover:bg-white/[0.06]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2">
            {rules.map((r, i) => {
              const ctrl = byId.get(r.questionId);
              const choices = choicesForQuestion(ctrl);
              const ops = opsForQuestion(ctrl);
              const needsValue = !VALUELESS_OPS.includes(r.op);
              const multi = MULTI_VALUE_OPS.includes(r.op);
              const selected = Array.isArray(r.value)
                ? r.value.map(String)
                : r.value === "" || r.value === undefined
                ? []
                : [String(r.value)];

              // Ticking a second answer must ADD to the rule, never replace the
              // first — "show this for Yes-A or Yes-B" is the single most
              // common thing people write here. The operator widens from
              // "is" to "is any of" (and narrows back) on its own, so the user
              // never has to know that distinction exists.
              const canPickSeveral = isChoiceOp(r.op);
              const toggleChoice = (v) => {
                if (!canPickSeveral && !multi) {
                  setRule(i, { value: selected.includes(v) ? "" : v });
                  return;
                }
                const next = selected.includes(v)
                  ? selected.filter((s) => s !== v)
                  : [...selected, v];
                if (!canPickSeveral) {
                  setRule(i, { value: next });
                  return;
                }
                const plural = PLURAL_OP[r.op] || r.op;
                const singular = SINGULAR_OP[r.op] || r.op;
                setRule(
                  i,
                  next.length > 1
                    ? { op: plural, value: next }
                    : { op: singular, value: next[0] ?? "" }
                );
              };

              return (
                <div
                  key={i} // eslint-disable-line react/no-array-index-key
                  className="rounded-lg border border-white/10 bg-white/[0.02] p-2 space-y-1.5"
                >
                  <div className="flex items-center gap-1.5">
                    <span className="shrink-0 text-[10px] uppercase tracking-wider text-slate-500 w-8">
                      {i === 0 ? "If" : mode === "any" ? "or" : "and"}
                    </span>
                    <select
                      className={`${selectCls} flex-1 min-w-0 !py-1.5 text-xs`}
                      value={r.questionId || ""}
                      onChange={(e) =>
                        // Changing the controlling field invalidates any value
                        // picked from the old field's options.
                        setRule(i, { questionId: e.target.value, value: "" })
                      }
                    >
                      <option value="" className="bg-[#0b0820]">
                        Choose a question…
                      </option>
                      {optionGroups
                        ? optionGroups.map(([label, items]) => (
                            <optgroup
                              key={label}
                              label={label}
                              className="bg-[#0b0820]"
                            >
                              {items.map((c) => (
                                <option
                                  key={c.id}
                                  value={c.id}
                                  className="bg-[#0b0820]"
                                >
                                  {c.question || "(untitled field)"}
                                </option>
                              ))}
                            </optgroup>
                          ))
                        : pickList.map((c) => (
                            <option
                              key={c.id}
                              value={c.id}
                              className="bg-[#0b0820]"
                            >
                              {c.question || "(untitled field)"}
                            </option>
                          ))}
                    </select>
                    <select
                      className={`${selectCls} shrink-0 w-36 !py-1.5 text-xs`}
                      value={r.op}
                      onChange={(e) => {
                        const nextOp = e.target.value;
                        // Moving between "is" and "is any of" is the same
                        // question about the same answers, so the choices
                        // already ticked survive. Any other switch changes what
                        // a value even means, so it starts clean.
                        const keep = isChoiceOp(r.op) && isChoiceOp(nextOp);
                        setRule(i, {
                          op: nextOp,
                          value: keep
                            ? MULTI_VALUE_OPS.includes(nextOp)
                              ? selected
                              : selected[0] ?? ""
                            : "",
                        });
                      }}
                    >
                      {ops.map((op) => (
                        <option key={op} value={op} className="bg-[#0b0820]">
                          {OP_LABELS[op] || op}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeRule(i)}
                      title="Delete this rule"
                      className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-300 hover:bg-rose-500/15 transition"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {needsValue &&
                    (choices.length ? (
                      <div className="flex flex-wrap gap-1 pl-9">
                        {choices.map((c) => (
                          <button
                            key={c.value}
                            type="button"
                            onClick={() => toggleChoice(c.value)}
                            className={`px-2 py-1 rounded-md text-[11px] border transition ${
                              selected.includes(c.value)
                                ? "bg-purple-500/30 border-purple-300/50 text-white"
                                : "bg-white/[0.04] border-white/10 text-slate-300 hover:bg-white/[0.09]"
                            }`}
                          >
                            {c.label}
                          </button>
                        ))}
                        {(multi || canPickSeveral) && (
                          <span className="self-center text-[10px] text-slate-500 ml-1">
                            {selected.length > 1 ? "any of these" : "tick any that apply"}
                          </span>
                        )}
                      </div>
                    ) : (
                      <input
                        className={`${inputCls} ml-9 !py-1.5 text-xs`}
                        style={{ width: "calc(100% - 2.25rem)" }}
                        value={Array.isArray(r.value) ? r.value.join(", ") : r.value ?? ""}
                        onChange={(e) =>
                          setRule(
                            i,
                            multi
                              ? {
                                  value: e.target.value
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter(Boolean),
                                }
                              : { value: e.target.value }
                          )
                        }
                        placeholder={
                          multi ? "Answers, separated by commas" : "The answer"
                        }
                      />
                    ))}

                  {ctrl && isMultiAnswer(ctrl) && mode === "all" && rules.length > 1 && (
                    <p className="pl-9 text-[10px] text-fuchsia-200/90">
                      Every box must be ticked at once — choose “Just one” if
                      either should do.
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <button
              type="button"
              onClick={addRule}
              disabled={pickList.length === 0}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-emerald-200 bg-emerald-400/10 border border-emerald-400/25 hover:bg-emerald-400/20 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <Plus className="w-3 h-3" /> Add another condition
            </button>

            {/* The escape hatch. Off by default — a rule almost always concerns
                the section it's in — but one click away when it doesn't. */}
            {allCandidates.length > candidates.length && (
              <label
                className={`inline-flex items-center gap-1.5 text-[11px] select-none ${
                  hasOutOfSectionRule
                    ? "text-slate-500 cursor-default"
                    : "text-slate-400 hover:text-slate-200 cursor-pointer"
                }`}
                title={
                  hasOutOfSectionRule
                    ? "A condition here already uses a question from another section, so the full list stays on."
                    : "Also list questions from the other sections of this visit"
                }
              >
                <input
                  type="checkbox"
                  checked={listAll}
                  disabled={hasOutOfSectionRule}
                  onChange={(e) => setLookOutside(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-white/20 bg-white/[0.04] accent-purple-500 disabled:opacity-60"
                />
                Use a question from another section
              </label>
            )}
          </div>

          {listAll && (
            <p className="text-[10px] text-slate-500">
              Showing every question in this visit, grouped by section.
            </p>
          )}

          {/* The rows already read as sentences and the saved rule is spelled
              out on the field itself, so the only thing worth saying twice is
              a rule that won't do anything yet. */}
          {rules.some(
            (r) =>
              !r.questionId ||
              (!VALUELESS_OPS.includes(r.op) &&
                (Array.isArray(r.value) ? r.value.length === 0 : !r.value))
          ) && (
            <p className="text-[10px] text-fuchsia-200/90">
              A condition is unfinished and will be ignored.
            </p>
          )}
        </>
      )}
    </div>
  );
}
